import { api } from '@/integrations/clients/carAuction/carAuctionClient';
import { 
  Company, 
  CreateCompanyRequest, 
  UpdateCompanyRequest,
  CreateCompanyResponse,
  UpdateCompanyResponse,
  DeleteCompanyResponse
} from '@/types/company';

export class CompanyManagementService {
  // Get all companies with pagination (admin can see all data)
  static async getAllCompanies(
    page: number = 1,
    limit: number = 15,
    searchQuery?: string,
    statuses?: string[],
  ): Promise<{
    companies: Company[];
    totalCount: number;
    totalPages: number;
    currentPage: number;
  }> {
    const searchParam = searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : '';
    const statusParam =
      statuses && statuses.length > 0
        ? `&status=${encodeURIComponent(statuses.join(','))}`
        : '';
    const response = await api.get<{
      success: true;
      companies: Company[];
      totalCount: number;
      totalPages: number;
      currentPage: number;
    }>(`/admin/companies?page=${page}&limit=${limit}${searchParam}${statusParam}`);
    
    return {
      companies: response.companies,
      totalCount: response.totalCount,
      totalPages: response.totalPages,
      currentPage: response.currentPage,
    };
  }

  // Get a specific company by ID
  static async getCompanyById(id: string): Promise<Company> {
    const response = await api.get<{
      success: true;
      data: Company;
    }>(`/admin/companies/${id}`);
    
    return response.data;
  }

  // Create a new company
  static async createCompany(data: CreateCompanyRequest): Promise<CreateCompanyResponse> {
    const response = await api.post<{
      success: true;
      data: Company;
    }>('/admin/companies', data);
    
    return {
      id: response.data._id,
      status: 'created',
      message: '', // Message is handled by i18n in UI components
      createdAt: response.data.created_at,
    };
  }

  // Update an existing company
  static async updateCompany(data: UpdateCompanyRequest): Promise<UpdateCompanyResponse> {
    const { _id, ...updateData } = data;
    const response = await api.put<{
      success: true;
      data: Company;
    }>(`/admin/companies/${_id}`, updateData);
    
    return {
      id: response.data._id,
      status: 'updated',
      message: '', // Message is handled by i18n in UI components
      updatedAt: response.data.created_at,
    };
  }

  static async updateCompanyStatus(id: string, status: string): Promise<Company> {
    const response = await api.patch<{
      success: true;
      data: Company;
    }>(`/admin/companies/${id}/status`, { status });
    return response.data;
  }

  // Delete a company
  static async deleteCompany(id: string): Promise<DeleteCompanyResponse> {
    const response = await api.delete<{
      success: true;
      message: string;
    }>(`/admin/companies/${id}`);
    
    return {
      status: 'success',
      message: '', // Message is handled by i18n in UI components
    };
  }

  // Get members of a company
  static async getMembersOfCompany(companyId: string): Promise<{
    members: Array<{
      _id: string;
      username: string;
      email: string;
      role: 'individual' | 'dealer' | 'business' | 'admin';
      firstName?: string | null;
      lastName?: string | null;
      phoneNumber?: string | null;
      bankIdVerified: boolean;
      isActive: boolean;
      lastLogin?: string | null;
      createdAt: string;
      updatedAt: string;
    }>;
    totalCount: number;
  }> {
    const response = await api.get<{
      success: true;
      members: Array<{
        _id: string;
        username: string;
        email: string;
        role: 'individual' | 'dealer' | 'business' | 'admin';
        firstName?: string | null;
        lastName?: string | null;
        phoneNumber?: string | null;
        bankIdVerified: boolean;
        isActive: boolean;
        lastLogin?: string | null;
        createdAt: string;
        updatedAt: string;
      }>;
      totalCount: number;
    }>(`/admin/companies/${companyId}/members`);
    
    return response;
  }

  static async addComment(
    companyId: string,
    text: string,
  ): Promise<Company> {
    const response = await api.post<{ success: true; data: Company }>(
      `/admin/companies/${companyId}/comments`,
      { text },
    );
    return response.data;
  }

  static async updateComment(
    companyId: string,
    commentId: string,
    text: string,
  ): Promise<Company> {
    const response = await api.put<{ success: true; data: Company }>(
      `/admin/companies/${companyId}/comments/${commentId}`,
      { text },
    );
    return response.data;
  }

  static async deleteComment(
    companyId: string,
    commentId: string,
  ): Promise<Company> {
    const response = await api.delete<{ success: true; data: Company }>(
      `/admin/companies/${companyId}/comments/${commentId}`,
    );
    return response.data;
  }
}
